import { memo, useMemo, useState, useCallback } from 'react';
import { ProgressBar, useTheme } from '@tw/tw-component-library';
import { ProgressBarWrapper } from './BodyLayout.styles';
import { PageState } from '../../../hooks/usePageState';
import { CurrentUser, SectionKey } from '../../../types';
import { sectionKeyToDisplay } from '../../../types';
import {
  getWorkflowTypeFromDisplayName,
  PaymentWorkflowType,
  requiresClientMatterData,
} from '../../../components/container/forms/types/api/graph.types';
import {
  canReassign,
  isUserResponsibleLawyer,
} from '../../../utils/permissions';
import { SectionProgressSummary } from '@/components/container/forms/hooks/useFormState';
import { useSingleWorkflowPrint } from '../../../hooks/useSingleWorkflowPrint';
import { AuthContextProps } from '../../../contexts/AuthContext';
import { PrintOptionsModal, PrintOptions } from './PrintOptionsModal';

// ============= Types =============
interface FormState {
  isRejected: boolean;
  isCancelled: boolean;
  isComplete: boolean;
  workflowType?: PaymentWorkflowType;
  data?: {
    id?: number;
    responsible_lawyer?: unknown[];
  };
}

interface ProgressBarManagerProps {
  pageState: PageState;
  progressSection: SectionKey;
  sectionProgress: SectionProgressSummary;
  formState: FormState;
  currentUser: CurrentUser | null;
  isCancelling: boolean;
  onBackToWorkflows: () => void;
  onReassign: () => void;
  onCancelModalOpen: () => void;
  onModalOpen: () => void;
  onCreateRequest?: (workflowType?: PaymentWorkflowType) => Promise<void>;
  allowedWorkflowTypeEnums: PaymentWorkflowType[];
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
}

// ============= Component =============
/**
 * ProgressBarManager Component
 * Manages the progress bar display and actions based on current page state
 */
export const ProgressBarManager = memo<ProgressBarManagerProps>(
  ({
    pageState,
    progressSection,
    sectionProgress,
    formState,
    currentUser,
    isCancelling,
    onBackToWorkflows,
    onReassign,
    onCancelModalOpen,
    onModalOpen,
    onCreateRequest,
    allowedWorkflowTypeEnums,
    getAccessToken,
    spoofedUser,
  }) => {
    const { theme } = useTheme();
    const canUserReassign =
      canReassign(currentUser) ||
      ((currentUser || spoofedUser) &&
        isUserResponsibleLawyer(
          formState.data?.responsible_lawyer,
          currentUser,
          spoofedUser
        ));

    const isFormCompleted = sectionProgress.completed === sectionProgress.total;

    // Print functionality
    const workflowId = formState.data?.id?.toString() || '';
    const workflowType =
      formState.workflowType || PaymentWorkflowType.Undefined;
    const { isPrinting, printWorkflow } = useSingleWorkflowPrint({
      getAccessToken,
      spoofedUser,
      workflowId,
      workflowType,
    });

    // Print modal state
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

    // Loading state
    const [isLoading, setIsLoading] = useState(false);

    const handlePrintClick = useCallback(() => {
      setIsPrintModalOpen(true);
    }, []);

    const handlePrintWithOptions = useCallback(
      async (options: PrintOptions) => {
        await printWorkflow(options);
        setIsPrintModalOpen(false);
      },
      [printWorkflow]
    );

    const handlePrintModalClose = useCallback(() => {
      setIsPrintModalOpen(false);
    }, []);

    const progressBarProps = useMemo(() => {
      if (pageState.isRequestDetailsPage) {
        // Get the current workflow configuration to determine if it's single or multi-section
        const currentWorkflowConfig = pageState.currentWorkflowConfig;
        const isSingleSectionForm = currentWorkflowConfig
          ? !currentWorkflowConfig.isMultiSection
          : false;

        if (isSingleSectionForm) {
          // For single-section forms, show "Rejected" or "Completed"
          const actions: Array<{
            label: string;
            onClick: () => void;
            disabled?: boolean;
          }> = [
            {
              label: 'Back',
              onClick: onBackToWorkflows,
            },
          ];

          // Add Print button if workflow ID exists
          if (workflowId && workflowType !== PaymentWorkflowType.Undefined) {
            actions.push({
              label: isPrinting ? 'Printing...' : 'Print',
              onClick: handlePrintClick,
              disabled: isPrinting,
            });
          }

          return {
            texts: [
              {
                content: formState.isCancelled
                  ? 'Cancelled'
                  : formState.isRejected
                  ? 'Rejected'
                  : formState.isComplete
                  ? 'Completed'
                  : 'In Progress',
                position: 'left' as const,
              },
            ],
            actions,
            animate: false,
          };
        } else {
          // For multi-section forms, show section progress
          const actions: Array<{
            label: string;
            onClick: () => void;
            backgroundColor?: string;
            color?: string;
            disabled?: boolean;
          }> = [
            {
              label: 'Back to Workflows',
              onClick: onBackToWorkflows,
            },
          ];

          // Only show Reassign button for specific sections and if user has permission
          const reassignableSections: SectionKey[] = [
            'resp_lawyer',
            'first_partner',
            'second_partner',
            'second_partner_above_25k',
            'second_partner_below_25k',
          ];
          if (
            reassignableSections.includes(progressSection) &&
            canUserReassign
          ) {
            actions.push({
              label: 'Reassign',
              onClick: onReassign,
            });
          }

          if (workflowId && workflowType !== PaymentWorkflowType.Undefined) {
            actions.push({
              label: isPrinting ? 'Printing...' : 'Print',
              onClick: handlePrintClick,
              disabled: isPrinting,
              backgroundColor: theme.colors.success,
            });
          }

          // Add Cancel button if form is not cancelled and user has permission
          if (canUserReassign && !formState.isCancelled && !isFormCompleted) {
            actions.push({
              label: isCancelling ? 'Cancelling...' : 'Cancel',
              onClick: onCancelModalOpen,
              backgroundColor: theme.colors.error,
            });
          }

          return {
            texts: [
              {
                content: formState.isCancelled
                  ? 'Cancelled'
                  : formState.isRejected
                  ? 'Rejected'
                  : formState.isComplete
                  ? 'Completed'
                  : sectionKeyToDisplay[progressSection]
                  ? `Progress: ${sectionKeyToDisplay[progressSection]}`
                  : 'In Progress',
                position: 'left' as const,
              },
            ],
            actions,
            animate: false,
            isRejected: formState.isRejected,
          };
        }
      }

      if (pageState.isDashboardPage) {
        // For dashboard, always open modal since workflow type needs to be selected
        return {
          actions: [
            {
              label: 'Create Workflow',
              onClick: onModalOpen,
            },
          ],
          animate: false,
          showPercentage: false,
        };
      }

      if (pageState.isActivitiesPage) {
        return {
          texts: [
            {
              content: 'Activities',
              position: 'left' as const,
            },
          ],
          actions: [
            {
              label: 'Back to Request',
              onClick: onBackToWorkflows,
            },
          ],
          animate: false,
          showPercentage: false,
        };
      }

      if (pageState.isFilesPage) {
        return {
          texts: [
            {
              content: 'Files',
              position: 'left' as const,
            },
          ],
          actions: [
            {
              label: 'Back to Request',
              onClick: onBackToWorkflows,
            },
          ],
          animate: false,
          showPercentage: false,
        };
      }

      // For workflow pages, we can customize the progress bar based on the workflow
      if (pageState.isWorkflowPage && pageState.currentWorkflow) {
        const workflowType = getWorkflowTypeFromDisplayName(
          pageState.currentWorkflow.name
        );
        const shouldShowCreateNew =
          allowedWorkflowTypeEnums.includes(workflowType);

        // Handler for Create New button - check if workflow requires client/matter data
        const handleCreateNew = () => {
          if (!onCreateRequest) {
            // Fallback to opening modal if onCreateRequest is not provided
            onModalOpen();
            return;
          }

          // If workflow doesn't require client/matter data, create directly
          if (!requiresClientMatterData(workflowType)) {
            setIsLoading(true);
            onCreateRequest(workflowType);
            setIsLoading(false);
          } else {
            // Otherwise, open the modal for client/matter selection
            onModalOpen();
          }
        };

        return {
          texts: [
            {
              content: `${pageState.currentWorkflow.name}`,
              position: 'left' as const,
            },
          ],
          actions: [
            {
              label: 'Create New',
              onClick: handleCreateNew,
              disabled: !shouldShowCreateNew,
              isLoading: isLoading,
            },
          ],
          animate: false,
          showPercentage: false,
          isRejected: formState.isRejected,
        };
      }

      return null;
    }, [
      pageState,
      progressSection,
      formState.isRejected,
      formState.isCancelled,
      formState.isComplete,
      isCancelling,
      canUserReassign,
      allowedWorkflowTypeEnums,
      onBackToWorkflows,
      onReassign,
      onCancelModalOpen,
      onModalOpen,
      onCreateRequest,
      workflowId,
      workflowType,
      isPrinting,
      isFormCompleted,
      handlePrintClick,
      isLoading,
      theme,
    ]);

    if (!progressBarProps) return null;

    return (
      <>
        <ProgressBarWrapper>
          <ProgressBar {...progressBarProps} />
        </ProgressBarWrapper>
        {workflowId && workflowType !== PaymentWorkflowType.Undefined && (
          <PrintOptionsModal
            isOpen={isPrintModalOpen}
            onClose={handlePrintModalClose}
            onPrint={handlePrintWithOptions}
            isPrinting={isPrinting}
          />
        )}
      </>
    );
  }
);

ProgressBarManager.displayName = 'ProgressBarManager';
